import { SEARCH_RESULT_CATEGORY, SEARCH_RESULT_TYPE, SEARCHABLE_ITEM_ID, SearchableItemMetadata } from './types'
import {
  SCHEDULE_COMPLEX_CARE_CALL_LINK,
  SCHEDULE_DBT_CALL_LINK,
} from '../common/constants/complexCareMicrositeConstants'
import { BRANCH_APP_LINK } from '../common/constants/constants'
import {
  HEALTH_CHALLENGES_BASE_ROUTE,
  NEURODIVERSITY_BASE_ROUTE,
  PAGE_ROUTES,
  WELLNESS_TOOLKIT_BASE_ROUTE,
} from '../common/constants/routingConstants'

/**
 * P0 Searchable items for Universal Search Phase 1.
 * Ordered by priority within each category.
 *
 * ELIGIBILITY CONDITIONS:
 * Each item may have conditions that determine visibility. These are documented
 * inline with each entry. Common condition types:
 * - Feature flags (LaunchDarkly)
 * - Customer properties (customerPropertiesMap)
 * - User properties (age, international status, etc.)
 * - Program eligibility (customerSupportedPrograms)
 */
export const SEARCHABLE_ITEMS: SearchableItemMetadata[] = [
  // ============ TRIAGE ============
  {
    id: SEARCHABLE_ITEM_ID.ADULT_TRIAGE,
    displayName: 'Search for care',
    keywords: [
      'therapy',
      'psychologist',
      'counselling',
      'coaching',
      'life coach',
      'triage',
      'provider search',
      'new provider',
      'psychotherapy',
    ], // TODO: include conditions from triage service (primary need and word cloud terms)
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.ONBOARD.ABOUT,
  },
  {
    id: SEARCHABLE_ITEM_ID.CHILD_TRIAGE,
    displayName: 'Child & teen care',
    keywords: [
      'child therapy',
      'toddler therapy',
      'adolescent therapy',
      'teen therapy',
      'child counseling',
      'toddler counseling',
      'adolescent counseling',
      'teen counseling',
      'child psychologist',
      'child psychotherapy',
    ], // TODO: include conditions from triage service (primary need and word cloud terms)
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.ONBOARD.ABOUT_CHILD,
  },
  {
    id: SEARCHABLE_ITEM_ID.FORKED_TRIAGE,
    displayName: 'Care options',
    keywords: ['care options', 'programs'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE,
  },

  // ============ PROGRAMS ============
  // All programs use strictMatchOnly to prevent vague searches from surfacing sensitive programs
  {
    id: SEARCHABLE_ITEM_ID.COACHING,
    displayName: 'Mental Health Coaching',
    keywords: ['coaching'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.MODAL,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_ITEM_ID.PARENTING_COACHING,
    displayName: 'Coaching for Parents',
    keywords: ['coaching', 'parent', 'tantrum'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.MODAL,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_ITEM_ID.COUPLES_THERAPY,
    displayName: 'Couples therapy',
    keywords: ['couples', 'relationship'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.MODAL,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_ITEM_ID.FAMILY_THERAPY,
    displayName: 'Family therapy',
    keywords: ['family', 'relationship', 'caregiver'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.MODAL,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_ITEM_ID.MEDICATION_CONSULT,
    displayName: 'Medication consultation',
    keywords: ['meds', 'medication', 'prescription', 'prescriber', 'drug'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.MODAL,
    strictMatchOnly: true,
  },
  {
    id: SEARCHABLE_ITEM_ID.RENEW_AUD,
    displayName: 'Alcohol & substance support',
    keywords: ['substance abuse', 'alcohol', 'SUD', 'drug'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.MODAL,
    strictMatchOnly: true,
  },

  // ============ COMPLEX CARE / EXTERNAL ============
  {
    id: SEARCHABLE_ITEM_ID.COMPLEX_CARE,
    displayName: 'Complex Care',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.OTHER_BENEFITS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.COMPLEX_CARE,
  },
  {
    id: SEARCHABLE_ITEM_ID.DBT,
    displayName: 'DBT (support for self-harm)',
    keywords: ['DBT', 'Dialectical Behavior Therapy', 'BPD', 'Borderline Personality Disorder'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.EXTERNAL_URL,
    url: SCHEDULE_DBT_CALL_LINK,
  },
  {
    id: SEARCHABLE_ITEM_ID.IOP,
    displayName: 'Intensive Outpatient Program (IOP)',
    keywords: ['IOP', 'facility', 'intensive outpatient program', 'intensive outpatient treatment'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.EXTERNAL_URL,
    url: SCHEDULE_COMPLEX_CARE_CALL_LINK,
  },
  {
    id: SEARCHABLE_ITEM_ID.PHP,
    displayName: 'Partial Hospitalization Program (PHP)',
    keywords: ['PHP', 'partial', 'partial hospitalization'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.EXTERNAL_URL,
    url: SCHEDULE_COMPLEX_CARE_CALL_LINK,
  },
  {
    id: SEARCHABLE_ITEM_ID.RESIDENTIAL,
    displayName: 'Residential Treatment',
    keywords: ['residential', 'facility'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.EXTERNAL_URL,
    url: SCHEDULE_COMPLEX_CARE_CALL_LINK,
  },
  {
    id: SEARCHABLE_ITEM_ID.INPATIENT,
    displayName: 'Inpatient Hospitalization',
    keywords: ['hospital', 'hospitalization', 'inpatient hospital', 'inpatient program'],
    category: SEARCH_RESULT_CATEGORY.CARE_OPTIONS,
    type: SEARCH_RESULT_TYPE.EXTERNAL_URL,
    url: SCHEDULE_COMPLEX_CARE_CALL_LINK,
  },

  // ============ TOOLKITS ============
  // All toolkits use WellnessToolkitAvailableConfig system with conditions including:
  // - Feature flags (per toolkit)
  // - Not teen, not international, English display language
  // - Provider supply available, not in active episode, not T3 recommendation
  // See useIsWellnessToolkitAvailable hook and getWellnessToolkitAvailableConfig for full logic
  {
    id: SEARCHABLE_ITEM_ID.TOOLKIT_RELATIONSHIPS,
    displayName: 'Relationships',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.TOOLKIT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/relationships`,
  },
  {
    id: SEARCHABLE_ITEM_ID.TOOLKIT_WORK,
    displayName: 'Work',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.TOOLKIT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/work`,
  },
  {
    id: SEARCHABLE_ITEM_ID.TOOLKIT_FAMILY,
    displayName: 'Family Hub',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.TOOLKIT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.FAMILY_HUB,
  },
  {
    id: SEARCHABLE_ITEM_ID.TOOLKIT_NEURODIVERSITY,
    displayName: 'Neurodiversity',
    keywords: ['neurodivergent', 'neurodiversity', 'neuropsych consult', 'neuro', 'autism', 'ASD', 'ADHD', 'autistic'],
    category: SEARCH_RESULT_CATEGORY.TOOLKIT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: NEURODIVERSITY_BASE_ROUTE,
  },
  {
    id: SEARCHABLE_ITEM_ID.TOOLKIT_HEALTH_CHALLENGES,
    displayName: 'Health challenges',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.TOOLKIT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: HEALTH_CHALLENGES_BASE_ROUTE,
  },
  {
    id: SEARCHABLE_ITEM_ID.TOOLKIT_PARENTING,
    displayName: 'Parenting',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.TOOLKIT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/parent`,
  },
  {
    id: SEARCHABLE_ITEM_ID.TOOLKIT_VALUES,
    displayName: 'Values',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.TOOLKIT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/purpose`,
  },
  {
    id: SEARCHABLE_ITEM_ID.TOOLKIT_TRAUMA,
    displayName: 'Trauma support',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.TOOLKIT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: `${WELLNESS_TOOLKIT_BASE_ROUTE}/trauma`,
  },

  // ============ CARE TAB ============
  {
    id: SEARCHABLE_ITEM_ID.CARE_TAB, // Probably need to remove
    displayName: 'Care',
    keywords: ['lessons', 'activities', 'appointment', 'session'],
    category: SEARCH_RESULT_CATEGORY.OTHER_BENEFITS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.ASSIGNMENTS_NEW, //replace this
  },
  {
    id: SEARCHABLE_ITEM_ID.SESSIONS, // Probably need to remove
    displayName: 'Sessions',
    keywords: ['sessions', 'appointment'],
    category: SEARCH_RESULT_CATEGORY.OTHER_BENEFITS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.SESSIONS,
  },

  // ============ LIBRARY ============
  {
    id: SEARCHABLE_ITEM_ID.LIBRARY,
    displayName: 'Library',
    keywords: ['self-care', 'resources', 'library', 'content', 'articles'],
    category: SEARCH_RESULT_CATEGORY.RESOURCES,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.ESSENTIALS,
  },
  {
    id: SEARCHABLE_ITEM_ID.EVENTS,
    displayName: 'Events',
    keywords: ['events', 'live events'],
    category: SEARCH_RESULT_CATEGORY.EVENTS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.ESSENTIALS_EVENTS,
  },
  {
    id: SEARCHABLE_ITEM_ID.MEDITATION,
    displayName: 'Meditation',
    keywords: ['meditate', 'meditation', 'mindfulness', 'sleep'],
    category: SEARCH_RESULT_CATEGORY.RESOURCES,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.ESSENTIALS,
  },

  // ============ BENEFITS ============
  {
    id: SEARCHABLE_ITEM_ID.BENEFITS, // Probably need to remove
    displayName: 'My benefits',
    keywords: ['care options', 'offerings', 'benefits', 'services'],
    category: SEARCH_RESULT_CATEGORY.OTHER_BENEFITS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.SEARCH_CARE.BENEFITS,
  },
  {
    id: SEARCHABLE_ITEM_ID.WELLNESS_CHECK_IN,
    displayName: 'Wellness check-in',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.OTHER_BENEFITS,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.WELLNESS_CHECK_IN.DEFAULT,
  },

  // ============ ACCOUNT ============
  {
    id: SEARCHABLE_ITEM_ID.ACCOUNT,
    displayName: 'Account',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.DEFAULT,
  },
  {
    id: SEARCHABLE_ITEM_ID.SETTINGS,
    displayName: 'Settings',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.SETTINGS,
  },
  {
    id: SEARCHABLE_ITEM_ID.SESSION_HISTORY,
    displayName: 'Session history',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.SESSION_HISTORY,
  },
  {
    id: SEARCHABLE_ITEM_ID.COVERAGE_INFO,
    displayName: 'Coverage information',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.COVERAGE,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.ELIGIBILITY,
  },
  {
    id: SEARCHABLE_ITEM_ID.HEALTH_PLAN_PAYMENT,
    displayName: 'Health plan and payment',
    keywords: ['credit card'],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.HEALTH_PLAN_AND_PAYMENTS,
  },
  {
    id: SEARCHABLE_ITEM_ID.PRIVACY_LEGAL,
    displayName: 'Privacy and Legal',
    keywords: ['consent'],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.PRIVACY_AND_LEGAL,
  },
  {
    id: SEARCHABLE_ITEM_ID.CLINICAL_DATA_SHARING,
    displayName: 'Share your information',
    keywords: ['health record', 'record request'],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.CLINICAL_DATA_SHARING,
  },
  {
    id: SEARCHABLE_ITEM_ID.ADD_DEPENDENT,
    displayName: 'Add a dependent',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.ADD_FAMILY_MEMBER,
  },
  {
    id: SEARCHABLE_ITEM_ID.CHANGE_EMAIL,
    displayName: 'Change email',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.CHANGE_EMAIL,
  },
  {
    id: SEARCHABLE_ITEM_ID.MFA,
    displayName: 'Two-factor authentication',
    keywords: ['MFA', '2FA'],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.ENROLL_MFA,
  },
  {
    id: SEARCHABLE_ITEM_ID.PHONE_NUMBER,
    displayName: 'Phone number',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.UPDATE_PHONE_NUMBER,
  },
  {
    id: SEARCHABLE_ITEM_ID.LOCATION_TIMEZONE,
    displayName: 'Location & timezone',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.UPDATE_LOCATION,
  },
  {
    id: SEARCHABLE_ITEM_ID.DISPLAY_LANGUAGE,
    displayName: 'Display language',
    keywords: ['language'],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.UPDATE_LANGUAGE,
  },
  {
    id: SEARCHABLE_ITEM_ID.NOTIFICATIONS,
    displayName: 'Notifications',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.UPDATE_NOTIFICATION_SETTINGS,
  },
  {
    id: SEARCHABLE_ITEM_ID.TRANSFER_ACCOUNT,
    displayName: 'Transfer your Lyra account',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.TRANSFER_ACCOUNT.FIND_EMPLOYER,
  },
  {
    id: SEARCHABLE_ITEM_ID.ADD_HEALTH_PLAN, // P1
    displayName: 'Add Health Plan',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.HPI_COVERAGE_CHECK.PROFILE.HEALTH_PLAN_FORM,
  },
  {
    id: SEARCHABLE_ITEM_ID.ADD_PAYMENT_METHOD, // P1
    displayName: 'Add payment method',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.HPI_COVERAGE_CHECK.PROFILE.PAYMENT_INFO_FORM,
  },
  {
    id: SEARCHABLE_ITEM_ID.LATE_CANCELLATION_PAYMENT, // P1
    displayName: 'Late cancellation and no-show payment',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.HPI_COVERAGE_CHECK.PROFILE.INFRACTION_PAYMENT_INFO_FORM,
  },
  {
    id: SEARCHABLE_ITEM_ID.FORGOT_PASSWORD,
    displayName: 'Password',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.ACCOUNT,
    type: SEARCH_RESULT_TYPE.STANDARD,
    route: PAGE_ROUTES.PROFILE.RESET_PASSWORD,
  },

  // ============ EXTERNAL LINKS ============
  {
    id: SEARCHABLE_ITEM_ID.LYRA_APP,
    displayName: 'Lyra Health app',
    keywords: [],
    category: SEARCH_RESULT_CATEGORY.OTHER_BENEFITS,
    type: SEARCH_RESULT_TYPE.EXTERNAL_URL,
    url: BRANCH_APP_LINK,
  },
]

/**
 * Items requiring strict matching (threshold 0.1).
 * Used for sensitive programs where vague searches should not surface the item.
 */
export const STRICT_MATCH_ITEMS = SEARCHABLE_ITEMS.filter((item) => item.strictMatchOnly)

/**
 * Items using standard fuzzy matching (threshold 0.4).
 */
export const STANDARD_ITEMS = SEARCHABLE_ITEMS.filter((item) => !item.strictMatchOnly)
